import type { DiagramDeserializedElements, DiagramSerializedElements } from '../state/DiagramElementSerializer';
import type { DiagramElement, DiagramManagerContextBase } from '../state/DiagramContext';
import type { Shape } from '../shapes/Shape';
import type { TextElement } from '../shapes/TextElement';
import { serializeDiagramElements } from '../state/DiagramElementSerializer';
import type { DiagramSelectionGroup } from '../state/diagramSelection';
import { forEachSelectedDiagramElement, getSelectedDiagramElements } from '../state/diagramSelection';
import { CLIPBOARD } from '../../../config/constants';

interface DiagramClipboardContext extends DiagramManagerContextBase {
  deserializeElements: (data: DiagramSerializedElements) => DiagramDeserializedElements;
  clearSelection: () => void;
  selectElement: (element: DiagramElement) => void;
  deleteSelected: () => void;
  fadeOutPlaceholder: () => void;
  notifyChange: () => void;
}

export class DiagramClipboardManager {
  private readonly context: DiagramClipboardContext;
  private clipboard: string | null = null;

  constructor(context: DiagramClipboardContext) {
    this.context = context;
  }

  private getSelectedElements(): DiagramSelectionGroup {
    const { shapes, connectors, textElements, selectedElements } = this.context.store;
    return getSelectedDiagramElements({
      selectedIds: selectedElements,
      shapes,
      connectors,
      textElements,
    });
  }

  private hasSelection(selection: DiagramSelectionGroup): boolean {
    return (
      selection.shapes.length > 0 ||
      selection.connectors.length > 0 ||
      selection.textElements.length > 0
    );
  }

  private addElementsToStore(elements: DiagramSelectionGroup): void {
    this.context.clearSelection();

    for (const shape of elements.shapes) {
      this.context.store.shapes.push(shape);
      this.context.selectElement(shape);
    }
    for (const connector of elements.connectors) {
      this.context.store.connectors.push(connector);
      this.context.selectElement(connector);
    }
    for (const textElement of elements.textElements) {
      this.context.store.textElements.push(textElement);
      this.context.selectElement(textElement);
    }

    if (this.hasSelection(elements)) {
      this.context.fadeOutPlaceholder();
    }
  }

  copySelected(): void {
    const selection = this.getSelectedElements();
    if (!this.hasSelection(selection)) {
      return;
    }

    this.clipboard = JSON.stringify(
      serializeDiagramElements(selection.shapes, selection.connectors, selection.textElements)
    );
  }

  cutSelected(): void {
    this.copySelected();
    this.context.deleteSelected();
  }

  paste(): void {
    if (!this.clipboard) return;

    try {
      const data = JSON.parse(this.clipboard);
      const { shapes: pastedShapes, connectors: pastedConnectors, textElements: pastedTextElements } =
        this.context.deserializeElements({
          shapes: data.shapes ?? [],
          connectors: data.connectors ?? [],
          textElements: data.textElements ?? [],
        });

      // Offset pasted elements slightly
      const offset = CLIPBOARD.pasteOffset;
      for (const shape of pastedShapes) {
        shape.position.x += offset;
        shape.position.y += offset;
      }
      for (const connector of pastedConnectors) {
        connector.startPoint.x += offset;
        connector.startPoint.y += offset;
        connector.endPoint.x += offset;
        connector.endPoint.y += offset;
        // Clear connection info since the connector is being offset
        connector.startShapeId = null;
        connector.endShapeId = null;
        connector.startConnectionPointId = null;
        connector.endConnectionPointId = null;
      }
      for (const textElement of pastedTextElements) {
        textElement.position.x += offset;
        textElement.position.y += offset;
      }

        this.addElementsToStore({
          shapes: pastedShapes,
          connectors: pastedConnectors,
          textElements: pastedTextElements,
        });

        this.context.notifyChange();
        this.context.render();
    } catch {
      // Invalid clipboard data
    }
  }

  duplicateSelected(): void {
    const { shapes, connectors, textElements, selectedElements } = this.context.store;
    const newShapes: Shape[] = [];
    const newTextElements: TextElement[] = [];
    forEachSelectedDiagramElement(
      { selectedIds: selectedElements, shapes, connectors, textElements },
      {
        shape: (shape) => {
          newShapes.push(shape.clone());
        },
        textElement: (textElement) => {
          newTextElements.push(textElement.clone());
        },
      }
    );

    this.addElementsToStore({
      shapes: newShapes,
      connectors: [],
      textElements: newTextElements,
    });

    this.context.notifyChange();
    this.context.render();
  }
}
